import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';

import { Card } from 'components/UI';
import { Marker } from '@react-google-maps/api';
import MapComponent from 'components/Map/Map';
import PolygonItem from 'components/Map/Polygon';
import { locationActions } from 'state/modules/locations';

import i18n from 'i18n-js';

const placeInput = React.createRef();

const DEFAULT_COORDS = {
  lat: 41.2145219,
  lng: -109.4779065,
};
const DEFAULT_MAPS_OPTIONS = {
  scrollwheel: false,
};

// The shared v4 Card expects a classes object ({ root }) for its CardContent.
// Define the class globally so the flush padding survives without makeStyles.
const cardContentFlushStyles = (
  <GlobalStyles
    styles={{
      '.lm-location-card-content-root': {
        padding: 0,
        paddingBottom: '0 !important',
      },
    }}
  />
);

const cardContentClasses = { root: 'lm-location-card-content-root' };

const LocationMap = ({ id, editable = false }) => {
  const dispatch = useDispatch();

  const [location, setLocation] = useState({});
  const [mapRef, setMapRef] = useState({});
  const [zone, setZone] = useState({});
  const locationData = useSelector((state) => state.locations?.location);

  const loadLocation = useCallback(
    (data) => {
      setZone((zone) => ({
        ...zone,
        zone: data.zone,
        lat: data.center.lat,
        lng: data.center.lng,
        address: data.address,
      }));

      setLocation(data.center);

      const bounds = getBounds(data);
      if (bounds && bounds.extend) {
        bounds.extend(data.center);
      }
      if (bounds && mapRef && mapRef.fitBounds) {
        mapRef.fitBounds(bounds);
      }
    },
    [mapRef]
  );

  useEffect(() => {
    if (id) {
      dispatch(locationActions.fetchLocationsById(id));
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (locationData && locationData.id) {
      loadLocation(locationData);
    }
  }, [locationData, loadLocation]);

  /**
   * Get new bounds to be used to center the map
   * @param {*} zones
   */
  const getBounds = (zones) => {
    let bounds = {};
    if (window && window.google && window.google.maps) {
      bounds = new window.google.maps.LatLngBounds();

      if (zones) {
        if (Array.isArray(zones)) {
          zones.forEach((zone) => {
            zone.zone.forEach((coords) =>
              coords.lat && coords.lng
                ? bounds.extend(coords)
                : bounds.extend({
                    lat: coords[0],
                    lng: coords[1],
                  })
            );
          });
        } else {
          //A single zone called zones
          zones.zone &&
            zones.zone.forEach((coords) =>
              coords.lat && coords.lng
                ? bounds.extend(coords)
                : bounds.extend({
                    lat: coords[0],
                    lng: coords[1],
                  })
            );
        }
      }
    }

    return bounds;
  };

  return (
    <Card
      title={i18n.t('locations.map')}
      headerActionType="vertical"
      contentRootClasses={cardContentClasses}
    >
      <>
        {cardContentFlushStyles}
        <Box sx={{ padding: '24px 32px' }}>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid size={12}>
              <Box>
                <MapComponent
                  center={DEFAULT_COORDS}
                  zoom={10}
                  defaultOptions={DEFAULT_MAPS_OPTIONS}
                  setMapRef={(ref) => setMapRef(ref)}
                >
                  {location && location.lat && location.lng && (
                    <Marker
                      position={{
                        lat: location.lat,
                        lng: location.lng,
                      }}
                    />
                  )}

                  {zone && zone.zone && (
                    <PolygonItem
                      path={zone.zone}
                      editable={editable}
                      options={{
                        fillColor: '#007bff',
                        strokeWeight: 4,
                        strokeColor: '#007bff',
                        fillOpacity: 0.2,
                      }}
                    />
                  )}
                </MapComponent>
                {/* required reference to get the detail of a place */}
                <div id="map-element" ref={placeInput} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </>
    </Card>
  );
};

LocationMap.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editable: PropTypes.bool,
};

export default LocationMap;
